import { RootBreadcrumb } from "#/components/talk-to-carrot/util/talkToCarrotMolecules"
import { Link as ReactRouterLink, Redirect, useLocation } from "react-router-dom"
import { Breadcrumbs, Breadcrumb, Box, Progress } from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { FormattedMessage } from "react-intl"
import { BookAgainExpertBio, BookAgainPageLayout } from "#/components/care-team/BookAgainPageLayout"
import {
  TimeTapScheduler,
  TimeTapSpecialistConfirmation,
  useReportToHeapTimeTapChatScheduled
} from "#/components/time-tap/TimeTapScheduler"
import { ExpertBioModal } from "#/components/expert-bios/expert-bio-modal"
import { CareTeamExpertWithTiles, shouldSkipServiceSelection } from "#/components/care-team/utils/careTeamUtil"
import { useTimeTapLink } from "#/lib/contentful/utils/useContentfulTiles"
import { DontSeeATimeThatWorksForYouTile } from "#/components/care-team/DontSeeATimeThatWorksForYouTile"
import { useResolvedTimeTapLink } from "#/components/talk-to-carrot/hooks/useResolvedTimeTapLink"
import { auto } from "#/services/locale/bidirectional-text"

export const BookAgainTimeTapSchedulerPage = (): JSX.Element => {
  const location = useLocation<{ expert: CareTeamExpertWithTiles; reasonId: number }>()
  const [showChatConfirmation, setShowChatConfirmation] = useState(false)
  const expert = location.state?.expert
  const reasonId = location.state?.reasonId

  const expertBio = expert?.expertBio
  const services = expert?.services
  const targetService = services?.find((service: { reasonId: number }) => service.reasonId === reasonId)

  const timeTapLink = useTimeTapLink(reasonId, targetService?.locationId)
  const resolvedTimeTapLink = useResolvedTimeTapLink(timeTapLink?.data)

  const shouldRedirectToLandingPage = shouldSkipServiceSelection(expert)

  useReportToHeapTimeTapChatScheduled(
    setShowChatConfirmation,
    {
      eventName: "Care Team expert chat scheduled",
      properties: {
        "Expert name": expertBio?.name,
        "Expert professionalId": expertBio?.professionalId,
        "Expert chat reasonId": targetService?.reasonId,
        "Expert chat locationId": targetService?.locationId
      }
    },
    Boolean(targetService?.reasonId) && Boolean(targetService?.locationId)
  )

  if (!expert || !reasonId) {
    return <Redirect to="/care-team" />
  }

  if (timeTapLink.isLoading || resolvedTimeTapLink.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ blockSize: "100vh" }}>
        <Progress />
      </Box>
    )
  }

  return (
    <BookAgainPageLayout
      breadcrumbs={
        <Breadcrumbs>
          <RootBreadcrumb />
          <Breadcrumb
            component={ReactRouterLink}
            to={{
              pathname: `/care-team/book-again`,
              state: !shouldRedirectToLandingPage ? { expert } : undefined
            }}
          >
            <FormattedMessage
              defaultMessage="Book Again: {specialistName}"
              values={{ specialistName: auto(expertBio.name) }}
            />
          </Breadcrumb>
          <Breadcrumb currentPage dir="auto">
            {targetService?.tileEntry?.title ? targetService?.tileEntry?.title : expertBio.name}
          </Breadcrumb>
        </Breadcrumbs>
      }
      content={
        <>
          <BookAgainExpertBio expertBio={expertBio} />
          <ExpertBioModal />
          <Box sx={{ blockSize: "52rem" }}>
            {showChatConfirmation ? (
              <TimeTapSpecialistConfirmation />
            ) : (
              <TimeTapScheduler
                reasonId={resolvedTimeTapLink.data?.fields?.reasonId}
                locationId={resolvedTimeTapLink.data?.fields?.locationId}
                timeTapEnvironment={resolvedTimeTapLink.data?.fields?.timeTapEnvironment ?? "Production"}
                staffId={expertBio?.professionalId}
              />
            )}
          </Box>
        </>
      }
      contactUsCard={<DontSeeATimeThatWorksForYouTile timeTapLink={timeTapLink.data} />}
    />
  )
}
